'use client'

import * as React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Button } from '@/components/ui/button'
import { Trash2, Plus, Info } from 'lucide-react'
import { ProductSelector } from '@/components/form/product-selector'
import OfferSelector from '@/components/config-editor/form-elements/OfferSelector'
import { type PostPurchaseRuleFormData, type PostPurchaseRuleLine, POST_PURCHASE_RULE_LINE_TYPES } from './validation'

export const PostPurchaseRuleForm = () => {
	const { t } = useTranslation('postPurchaseRules')
	const { id: storeId } = useParams<{ id: string }>()
	const {
		control,
		formState: { errors },
		watch,
		setValue,
	} = useFormContext<PostPurchaseRuleFormData>()

	const lines = watch('lines') || []
	const ruleOperator = watch('ruleOperator')
	const offers = watch('offers') || []
	const priority = watch('priority')

	const hasNonClubLine = lines.some((l) => l.type !== 'customer_did_not_subscribe_to_club')
	const cantAddLine = lines.length > 0 && lines.some((l) => l.type === 'customer_did_not_subscribe_to_club')
	const cantSelectOffer = lines.length === 0 || lines.some((l) => l.type === 'customer_did_not_subscribe_to_club')

	const getRuleTypeInfo = (type: PostPurchaseRuleLine['type']) => ({
		label: t(`form.conditions.types.${type}.label`),
		description: t(`form.conditions.types.${type}.description`),
	})

	const addLine = () => {
		const newLine: PostPurchaseRuleLine = {
			type: 'order_contains_product',
			productId: '',
			minimumQuantity: 1,
		}
		setValue('lines', [...lines, newLine], { shouldValidate: true })
	}

	const updateLine = (index: number, updatedLine: PostPurchaseRuleLine) => {
		const updated = [...lines]
		updated[index] = updatedLine
		setValue('lines', updated, { shouldValidate: true })
	}

	const removeLine = (index: number) => {
		const updated = lines.filter((_, i) => i !== index)
		setValue('lines', updated, { shouldValidate: true })
	}

	const handleLineTypeChange = (index: number, type: PostPurchaseRuleLine['type']) => {
		if (type === 'customer_did_not_subscribe_to_club') {
			setValue('lines', [{ type }], { shouldValidate: true })
			setValue('offers', [], { shouldValidate: true })
			return
		}

		const currentLine = lines[index]
		if (currentLine && 'productId' in currentLine) {
			updateLine(index, {
				type,
				productId: currentLine.productId || '',
				minimumQuantity: currentLine.minimumQuantity || 1,
			})
		} else {
			updateLine(index, {
				type,
				productId: '',
				minimumQuantity: 1,
			})
		}
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{t('form.ruleMatching.title')}</CardTitle>
					<CardDescription>
						{t('form.ruleMatching.description')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Controller
						name="ruleOperator"
						control={control}
						render={({ field }) => (
							<RadioGroup
								value={field.value}
								onValueChange={field.onChange}
								className="flex gap-4"
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="OR" id="operator-or" />
									<Label htmlFor="operator-or">{t('form.ruleMatching.or')}</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="AND" id="operator-and" />
									<Label htmlFor="operator-and">{t('form.ruleMatching.and')}</Label>
								</div>
							</RadioGroup>
						)}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('form.priority.title')}</CardTitle>
					<CardDescription>
						{t('form.priority.description')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Controller
						name="priority"
						control={control}
						render={({ field }) => (
							<div className="space-y-2">
								<Label htmlFor="priority">{t('form.priority.label')}</Label>
								<Input
									id="priority"
									type="number"
									min="1"
									value={field.value ?? ''}
									onChange={(e) => {
										const value = e.target.value
										field.onChange(value === '' ? null : Number.parseInt(value, 10))
									}}
									placeholder={t('form.priority.placeholder')}
								/>
								{errors.priority && (
									<p className="text-xs leading-tight text-red-600">{errors.priority.message as string}</p>
								)}
							</div>
						)}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('form.conditions.title')}</CardTitle>
					<CardDescription>
						{t('form.conditions.description')}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2 rounded-md bg-muted p-3 text-xs">
						{POST_PURCHASE_RULE_LINE_TYPES.map((type) => {
							const info = getRuleTypeInfo(type)
							return (
								<div key={type} className="flex gap-2">
									<Info className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" />
									<div>
										<span className="font-medium">{info.label}</span>
										<p className="text-muted-foreground">{info.description}</p>
									</div>
								</div>
							)
						})}
					</div>

					{lines.map((line, index) => (
						<div key={index} className="relative rounded-lg border p-4">
							<div className="space-y-4">
								<Select
									value={line.type}
									onValueChange={(value) => handleLineTypeChange(index, value as PostPurchaseRuleLine['type'])}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{POST_PURCHASE_RULE_LINE_TYPES.map((type) => {
											const info = getRuleTypeInfo(type)
											return (
												<SelectItem
													key={type}
													value={type}
													disabled={type === 'customer_did_not_subscribe_to_club' && lines.length > 1}
												>
													{info.label}
												</SelectItem>
											)
										})}
									</SelectContent>
								</Select>

								{line.type !== 'customer_did_not_subscribe_to_club' && 'productId' in line && (
									<div className="space-y-4">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<Label>{t('form.conditions.minimumQuantity')}</Label>
												<Input
													type="number"
													min="1"
													max="1000"
													value={line.minimumQuantity || 1}
													onChange={(e) => {
														const quantity = Number.parseInt(e.target.value, 10) || 1
														updateLine(index, {
															...line,
															minimumQuantity: quantity,
														})
													}}
												/>
											</div>
											<div>
												<ProductSelector
													storeId={storeId || ''}
													productId={line.productId}
													onProductChange={(productId) => {
														updateLine(index, {
															...line,
															productId,
														})
													}}
													label={t('form.conditions.product')}
													required
												/>
											</div>
										</div>
										<p className="text-muted-foreground text-xs">
											{t('form.conditions.quantityNote')}
										</p>
									</div>
								)}
							</div>

							<Button
								type="button"
								variant="destructive"
								size="icon"
								className="absolute -right-2 -top-2 h-6 w-6"
								onClick={() => removeLine(index)}
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						</div>
					))}

					<Button type="button" disabled={cantAddLine} onClick={addLine} variant="outline">
						<Plus className="mr-2 h-4 w-4" />
						{t('form.conditions.addCondition')}
					</Button>

					{errors.lines && (
						<p className="text-xs leading-tight text-red-600">{errors.lines.message as string}</p>
					)}
				</CardContent>
			</Card>

			{!cantSelectOffer && (
				<Card>
					<CardHeader>
						<CardTitle>{t('form.offers.title')}</CardTitle>
						<CardDescription>
							{t('form.offers.description')}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Controller
							name="offers"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<OfferSelector
										value={field.value?.[0] || ''}
										onChange={(offerId) => {
											field.onChange(offerId ? [offerId] : [])
										}}
										storeId={storeId}
									/>
									{errors.offers && (
										<p className="text-xs leading-tight text-red-600">{errors.offers.message as string}</p>
									)}
								</div>
							)}
						/>
					</CardContent>
				</Card>
			)}
		</div>
	)
}

